import { useEffect, useRef, useState } from "react";
import { useSearchParams, Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { CheckCircle2, Loader2, Mail, XCircle } from "lucide-react";
import { paymentStatus } from "../api/billing";
import { NeuCard } from "../components/neu/Neu";
import { useAuth } from "../context/AuthContext";
import "./PaymentResultPage.css";

export default function PaymentResultPage({ outcome = "success" }) {
  const { t } = useTranslation();
  const { user, signOut } = useAuth();
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const [state, setState] = useState(outcome === "cancel" ? "cancel" : "checking");
  const tries = useRef(0);

  useEffect(() => {
    if (outcome === "cancel" || !sessionId) {
      if (outcome !== "cancel") setState("failed");
      return undefined;
    }
    const timer = setInterval(async () => {
      tries.current += 1;
      const res = await paymentStatus(sessionId).catch(() => null);
      if (res?.payment_status === "paid") { setState("paid"); clearInterval(timer); }
      else if (tries.current >= 10) { setState("pending"); clearInterval(timer); }
    }, 2000);
    return () => clearInterval(timer);
  }, [sessionId, outcome]);

  const VIEW = {
    checking: [Loader2, "text-primary animate-spin", "pay.checking", null],
    paid: [CheckCircle2, "text-success", "pay.success", "pay.successBody"],
    cancel: [XCircle, "text-destructive", "pay.cancel", "pay.cancelBody"],
    pending: [Loader2, "text-primary", "pay.pending", null],
    failed: [XCircle, "text-destructive", "pay.failed", null],
  };
  const [Icon, cls, title, body] = VIEW[state];
  const steps = t("pay.steps", { returnObjects: true, defaultValue: [] });

  return (
    <div
      className={`flex min-h-[100dvh] items-center justify-center bg-background px-5 py-10 text-foreground ${
        outcome !== "cancel" ? "payment-success-light" : ""
      }`}
    >
      <NeuCard className="w-full max-w-md animate-pop text-center" data-testid="payment-result">
        <Icon className={`mx-auto h-12 w-12 ${cls}`} />
        <h1
          className={`mt-5 text-2xl font-black ${state === "paid" ? "text-emerald-700" : "text-foreground"}`}
          data-testid="payment-title"
        >
          {t(title)}
        </h1>
        {body ? <p className="mt-3 text-sm text-slate-600">{t(body)}</p> : null}

        {state === "paid" ? (
          <div className="mt-6 rounded-2xl bg-primary/10 p-5 text-start" data-testid="payment-check-email">
            <div className="flex items-center gap-2 text-primary font-bold">
              <Mail className="h-5 w-5" />{t("pay.checkInbox")}
            </div>
            <p className="mt-2 text-sm text-muted-foreground">{t("pay.checkInboxBody")}</p>
            {Array.isArray(steps) && steps.length ? (
              <ol className="mt-3 space-y-1 ps-5 text-sm list-decimal text-muted-foreground">
                {steps.map((s, i) => <li key={i}>{s}</li>)}
              </ol>
            ) : null}
            <p className="mt-3 text-xs text-muted-foreground">{t("pay.spamHint")}</p>
          </div>
        ) : null}

        {state === "paid" && user ? (
          <div
            className="mt-5 rounded-xl bg-amber-50 p-3 text-start"
            data-testid="payment-logout-hint"
          >
            <p className="text-xs font-semibold text-amber-900">
              {t("pay.differentAccount")}
            </p>
            <button
              type="button"
              onClick={signOut}
              data-testid="payment-logout"
              className="mt-1 text-xs font-bold text-amber-700 underline underline-offset-2"
            >
              {t("pay.signOutHere")}
            </button>
          </div>
        ) : null}
        {state === "cancel" || state === "failed" ? (
          <Link to="/login" className="mt-6 inline-block text-sm font-semibold text-primary" data-testid="payment-login">
            {t("pay.goLogin")}
          </Link>
        ) : null}
      </NeuCard>
    </div>
  );
}
